/**
 * Step 1 — Tenant typeahead.
 *
 * 250ms debounced search via ``fetchAdminTenants({q, limit: 10})``.
 * Mirror of the AdminApplicationsPage debounce pattern (lines 107-112).
 * Click-outside closes the dropdown. Click-result populates
 * state.selectedTenant.
 */
import React, { useEffect, useRef, useState } from "react";
import { fetchAdminTenants } from "../../../api/adminData";

const inputCls =
  "w-full rounded-[8px] border border-black/10 dark:border-white/[0.08] bg-slate-100 dark:bg-[#111520] px-3 py-2 text-sm text-[#0f172a] dark:text-[#eef2ff] outline-none";

function tenantDisplayName(t) {
  if (!t) return "";
  const fn = (t.first_name || "").trim();
  const ln = (t.last_name || "").trim();
  const full = `${fn} ${ln}`.trim();
  return full || (t.name || "").trim() || "(ohne Name)";
}

export default function Step1Tenant({ state, updateState }) {
  const [debouncedQ, setDebouncedQ] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Local 250ms debounce. Same shape as AdminApplicationsPage.js:107-112.
  useEffect(() => {
    const t = setTimeout(
      () => setDebouncedQ(state.tenantSearchQuery.trim()),
      250
    );
    return () => clearTimeout(t);
  }, [state.tenantSearchQuery]);

  // Fire fetch when debounced query changes (≥2 chars).
  useEffect(() => {
    if (state.selectedTenant) return; // skip while a tenant is locked in
    if (!debouncedQ || debouncedQ.length < 2) {
      updateState({ tenantSearchResults: [], tenantSearchLoading: false });
      return;
    }
    let cancelled = false;
    updateState({ tenantSearchLoading: true });
    fetchAdminTenants({ q: debouncedQ, limit: 10 })
      .then((items) => {
        if (cancelled) return;
        updateState({
          tenantSearchResults: Array.isArray(items) ? items : [],
          tenantSearchLoading: false,
        });
        setOpen(true);
      })
      .catch(() => {
        if (cancelled) return;
        updateState({
          tenantSearchResults: [],
          tenantSearchLoading: false,
        });
      });
    return () => {
      cancelled = true;
    };
    // updateState is stable enough; debouncedQ + selectedTenant cover the deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, state.selectedTenant]);

  // Click-outside closes dropdown.
  useEffect(() => {
    function handleClick(e) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  if (state.selectedTenant) {
    const t = state.selectedTenant;
    return (
      <div className="rounded-[10px] border border-black/10 bg-slate-50 p-4 dark:border-white/[0.07] dark:bg-[#0f1320]">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[14px] font-semibold text-[#0f172a] dark:text-[#eef2ff]">
              {tenantDisplayName(t)}
            </div>
            {t.email ? (
              <div className="mt-1 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
                {t.email}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() =>
              updateState({
                selectedTenant: null,
                tenantSearchQuery: "",
                tenantSearchResults: [],
                // Reset downstream state since tenant changed
                tenancies: null,
                selectedTenancy: null,
              })
            }
            className="text-[12px] font-semibold text-[#378ADD] hover:underline"
          >
            Ändern
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[1px] text-[#64748b] dark:text-[#6b7a9a]">
        Mieter suchen
      </label>
      <input
        type="text"
        value={state.tenantSearchQuery}
        onChange={(e) =>
          updateState({ tenantSearchQuery: e.target.value })
        }
        onFocus={() => {
          if (state.tenantSearchResults.length > 0) setOpen(true);
        }}
        placeholder="Name, E-Mail oder Telefon"
        className={inputCls}
        autoComplete="off"
      />
      {state.tenantSearchLoading ? (
        <div className="mt-1 text-[12px] text-[#64748b] dark:text-[#6b7a9a]">
          Suche…
        </div>
      ) : null}
      {open &&
      state.tenantSearchResults.length > 0 &&
      !state.tenantSearchLoading ? (
        <ul className="absolute left-0 right-0 z-10 mt-1 max-h-72 overflow-y-auto rounded-[8px] border border-black/10 bg-white shadow-lg dark:border-white/[0.08] dark:bg-[#141824]">
          {state.tenantSearchResults.map((t) => (
            <li key={t.id}>
              <button
                type="button"
                onClick={() =>
                  updateState({
                    selectedTenant: t,
                    tenantSearchQuery: "",
                    tenantSearchResults: [],
                  })
                }
                className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-[#1a2030]"
              >
                <span className="font-medium text-[#0f172a] dark:text-[#eef2ff]">
                  {tenantDisplayName(t)}
                </span>
                {t.email ? (
                  <span className="text-[11px] text-[#64748b] dark:text-[#6b7a9a]">
                    {t.email}
                  </span>
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      {open &&
      debouncedQ.length >= 2 &&
      !state.tenantSearchLoading &&
      state.tenantSearchResults.length === 0 ? (
        <div className="absolute left-0 right-0 z-10 mt-1 rounded-[8px] border border-black/10 bg-white px-3 py-2 text-sm text-[#64748b] shadow-lg dark:border-white/[0.08] dark:bg-[#141824] dark:text-[#6b7a9a]">
          Keine Mieter gefunden.
        </div>
      ) : null}
    </div>
  );
}
